import api from './api';
import { resolveSampleDataSource } from './systems/sample-local/sampleLocalMode';
import { sampleLocalStore } from './systems/sample-local/sampleLocalStore';

export interface StorageRoom {
  id: number;
  name: string;
  location: string | null;
  type: string | null;
  isShared?: boolean;
  userId?: number | null;
  user?: {
    id: number;
    firstName: string | null;
    lastName: string | null;
  } | null;
  samples?: Array<{
    id: number;
    name: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

export interface StorageRoomListResponse {
  success: boolean;
  data: StorageRoom[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const storageRoomService = {
  async list(params?: {
    page?: number;
    limit?: number;
    search?: string;
    type?: string;
    location?: string;
    adminViewAll?: boolean;
  }): Promise<StorageRoomListResponse> {
    if (resolveSampleDataSource() === 'local') return sampleLocalStore.listStorageRooms(params);
    const response = await api.get('/storage-rooms', { params });
    return response.data;
  },

  async getById(
    id: number,
    params?: { adminViewAll?: boolean }
  ): Promise<{ success: boolean; data: StorageRoom }> {
    if (resolveSampleDataSource() === 'local') return sampleLocalStore.getStorageRoom(id);
    const response = await api.get(`/storage-rooms/${id}`, { params });
    return response.data;
  },

  async create(data: {
    name: string;
    location?: string | null;
    type?: string | null;
    isShared?: boolean;
  }): Promise<{ success: boolean; data: StorageRoom }> {
    if (resolveSampleDataSource() === 'local') return sampleLocalStore.createStorageRoom(data);
    const response = await api.post('/storage-rooms', data);
    return response.data;
  },

  async update(
    id: number,
    data: {
      name?: string;
      location?: string | null;
      type?: string | null;
      isShared?: boolean;
    },
    options?: { adminViewAll?: boolean }
  ): Promise<{ success: boolean; data: StorageRoom }> {
    if (resolveSampleDataSource() === 'local') return sampleLocalStore.updateStorageRoom(id, data);
    const response = await api.put(`/storage-rooms/${id}`, data, {
      params: options?.adminViewAll ? { adminViewAll: 'true' } : undefined,
    });
    return response.data;
  },

  async delete(
    id: number,
    options?: { adminViewAll?: boolean }
  ): Promise<{ success: boolean; message: string }> {
    if (resolveSampleDataSource() === 'local') {
      sampleLocalStore.deleteStorageRoom(id);
      return { success: true, message: 'ok' };
    }
    const response = await api.delete(`/storage-rooms/${id}`, {
      params: options?.adminViewAll ? { adminViewAll: 'true' } : undefined,
    });
    return response.data;
  },

  async generateName(type: string): Promise<{ success: boolean; data: { name: string } }> {
    if (resolveSampleDataSource() === 'local') {
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      return { success: true, data: { name: `${type || 'Room'}-${stamp}` } };
    }
    const response = await api.get('/storage-rooms/generate-name', {
      params: { type },
    });
    return response.data;
  },
};

